import { copy } from "shared/assets";
import { buildUrl, IUserPermissions } from "shared/utils";
import { VENUE_PATHS } from "Venues/router";
import { IActionDropdownItem } from "shared/components";
import { IStatus } from "shared/types";
import { SuggestEditFormType } from "Venues/components";
import { VenueRouteParams } from "..";

const {
  venuePage: { edit },
  actions: actionsCopy,
  publishVenue,
  requestPublishVenue,
  unpublishVenue,
  venueEditors,
} = copy;

interface VenueBaseActionsArgs {
  venueId: string;
  history: any;
  userPermissions: IUserPermissions;
  openDeleteVenueModal: (id: string) => void;
}

interface VenuePageActionArgs extends VenueBaseActionsArgs {
  openPublishModal?: () => void;
  openRequestPublishModal?: () => void;
  openUnpublishModal?: () => void;
  openSuggestEditModal: (props: { type: SuggestEditFormType }) => void;
  pushRoute: (path: string, options?: VenueRouteParams) => void;
}

interface VenueDashboardActionArgs extends VenueBaseActionsArgs {
  openEditorsModal: () => void;
  openBandsModal?: () => void;
  openRequestPublishModal: () => void;
  openUnpublishModal: () => void;
}

export const getVenueActions = ({
  venueId,
  userPermissions,
  openDeleteVenueModal,
  openPublishModal,
  openRequestPublishModal,
  openUnpublishModal,
  openSuggestEditModal,
  pushRoute,
}: VenuePageActionArgs): IActionDropdownItem[] => {
  const actions: IActionDropdownItem[] = [];

  if (userPermissions.PUBLISH && openPublishModal) {
    actions.push({
      label: publishVenue.cta,
      handleClick: openPublishModal,
      status: IStatus.success,
      dataTestId: "publish",
    });
  }

  if (userPermissions.REQUEST_PUBLISH && openRequestPublishModal) {
    actions.push({
      label: requestPublishVenue.cta,
      handleClick: openRequestPublishModal,
      dataTestId: "request_publish",
    });
  }

  if (userPermissions.EDIT) {
    actions.push(
      {
        label: actionsCopy.edit,
        handleClick: () => pushRoute(VENUE_PATHS.EDIT_VENUE),
        dataTestId: "edit",
      },
      {
        label: edit.branding,
        handleClick: () => pushRoute(VENUE_PATHS.EDIT_VENUE_BRANDING),
        dataTestId: "branding",
      }
    );
  }

  if (userPermissions.UNPUBLISH && openUnpublishModal) {
    actions.push({
      label: unpublishVenue.cta,
      handleClick: openUnpublishModal,
      dataTestId: "unpublish",
    });
  }

  if (userPermissions.DELETE) {
    actions.push({
      label: actionsCopy.delete,
      status: IStatus.error,
      handleClick: () => openDeleteVenueModal(venueId),
      dataTestId: "delete",
    });
  }

  if (actions.length !== 0) {
    return actions;
  }

  return [
    {
      label: actionsCopy.suggestEdit,
      dataTestId: "suggest_edit",
      handleClick: () => openSuggestEditModal({ type: "Venue" }),
    },
  ];
};

export const getDashboardVenueActions = ({
  userPermissions,
  history,
  venueId,
  openDeleteVenueModal,
  openEditorsModal,
  openBandsModal,
  openRequestPublishModal,
  openUnpublishModal,
}: VenueDashboardActionArgs) => {
  const actions: IActionDropdownItem[] = [];

  if (userPermissions.REQUEST_PUBLISH) {
    actions.push({
      label: requestPublishVenue.cta,
      handleClick: openRequestPublishModal,
      dataTestId: "request_publish",
    });
  }

  if (userPermissions.EDIT) {
    actions.push({
      label: actionsCopy.edit,
      handleClick: () =>
        history.push(buildUrl(VENUE_PATHS.EDIT_VENUE, { venueId })),
      dataTestId: "edit",
    });
  }

  if (userPermissions.ADD_USER) {
    actions.push({
      label: venueEditors.cta,
      handleClick: openEditorsModal,
      dataTestId: "edit_editors",
    });
  }

  if (userPermissions.UNPUBLISH) {
    actions.push({
      label: unpublishVenue.cta,
      handleClick: openUnpublishModal,
      dataTestId: "unpublish",
    });
  }

  if (openBandsModal) {
    actions.push({
      label: "Edit bands",
      handleClick: openBandsModal,
      dataTestId: "unpublish",
    });
  }

  if (userPermissions.DELETE) {
    actions.push({
      label: actionsCopy.delete,
      status: IStatus.error,
      handleClick: () => openDeleteVenueModal(venueId),
      dataTestId: "delete",
    });
  }

  return actions;
};
